/**
 * Runtime application configuration.
 *
 * Vite inlines `import.meta.env.VITE_*` at BUILD time, so a pre-built image
 * cannot be re-pointed at a different API / OIDC host by changing env at deploy
 * time — the ConfigMap the Deployment injects is inert. To keep one image
 * usable on any host, endpoints are read at RUNTIME from `window.__APP_CONFIG__`,
 * which is served by `/config.js` (mounted from the `drone-fleet-config`
 * ConfigMap in k8s). Falls back to build-time env, then to sensible defaults
 * for `pnpm dev`.
 *
 * See .claude/tailscale-funnel-access.md for the "reconnecting"/wrong-URL story.
 */
export interface AppConfig {
  /** REST API base URL (no trailing path). */
  apiUrl: string;
  /** WebSocket base URL for the real-time gateway. */
  wsUrl: string;
  /** OIDC issuer (Dex) authority URL. */
  oidcAuthority: string;
  /** OIDC client id registered with Dex. */
  oidcClientId: string;
  /**
   * Leaflet tile URL template for the basemap (#574).
   *
   * Here rather than hardcoded in `styles/themes.ts` because the provider is an
   * operational choice, not a design one — and because a basemap can stop
   * working without a single line of this repository changing. CARTO began
   * requiring an API key and started stamping `API KEY REQUIRED` across every
   * dark tile; nothing broke in the build, and there was no way to repoint the
   * deployed image.
   *
   * `.env.example` had advertised `VITE_MAP_TILE_URL` for exactly this, and
   * nothing read it. Even wired, a `VITE_` variable alone would not have helped
   * here: Vite inlines it at build time, so the running image could not be
   * repointed without a rebuild — the trap this whole file exists to avoid.
   */
  mapTileUrl: string;
  /** Attribution HTML for whatever `mapTileUrl` points at. */
  mapAttribution: string;
  /**
   * Whether `mapTileUrl` already serves a dark basemap.
   *
   * The default provider is light in both themes and dark mode is produced by
   * inverting it in CSS (see `.basemap-dark` in globals.css). Point
   * `mapTileUrl` at a provider that is *already* dark — CARTO `dark_all` with a
   * key, say — and that inversion would turn it white. Set this and the
   * inversion is skipped.
   *
   * Explicit rather than sniffed from the URL: an override that silently breaks
   * dark mode is not an override, it is a trap, and guessing from a hostname is
   * how it would become one.
   */
  mapTilesAreDark: boolean;
}

interface RuntimeConfigWindow {
  __APP_CONFIG__?: Partial<AppConfig>;
}

const runtime: Partial<AppConfig> =
  (typeof window !== 'undefined' &&
    (window as unknown as RuntimeConfigWindow).__APP_CONFIG__) ||
  {};

const env = import.meta.env;

/**
 * The basemap this project ships with (#574).
 *
 * OpenStreetMap's standard tiles, in both themes, with no API key and no
 * account — dark mode inverts them in CSS rather than fetching a second,
 * differently-licensed source. One provider means the attribution below is
 * correct by construction rather than by remembering to keep it in step, which
 * is how dark mode came to be served by CARTO while crediting OSM.
 *
 * OSM's tile usage policy discourages heavy use. Fine for a fleet dashboard;
 * point `mapTileUrl` elsewhere before it is not.
 */
const DEFAULT_MAP_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const DEFAULT_MAP_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

/** Effective endpoints: runtime override → build-time env → dev default. */
export const config: AppConfig = {
  apiUrl: runtime.apiUrl || env.VITE_API_URL || 'http://localhost:8080',
  wsUrl: runtime.wsUrl || env.VITE_WS_URL || 'ws://localhost:8080',
  oidcAuthority:
    runtime.oidcAuthority ||
    env.VITE_OIDC_AUTHORITY ||
    'http://localhost:5556/dex',
  oidcClientId:
    runtime.oidcClientId || env.VITE_OIDC_CLIENT_ID || 'drone-fleet-ui',
  mapTileUrl:
    runtime.mapTileUrl || env.VITE_MAP_TILE_URL || DEFAULT_MAP_TILE_URL,
  mapAttribution:
    runtime.mapAttribution ||
    env.VITE_MAP_ATTRIBUTION ||
    DEFAULT_MAP_ATTRIBUTION,
  // `??`, not `||`: `false` is a meaningful value here and `||` would discard
  // it in favour of the next source.
  mapTilesAreDark:
    runtime.mapTilesAreDark ?? env.VITE_MAP_TILES_ARE_DARK === 'true',
};
